interface RateLimitConfig {
  maxAttempts: number
  windowMs: number
}

export const RATE_LIMITS = {
  signup: { maxAttempts: 3, windowMs: 60_000 },
  login: { maxAttempts: 5, windowMs: 60_000 },
  message: { maxAttempts: 10, windowMs: 60_000 },
  listingCreate: { maxAttempts: 5, windowMs: 300_000 },
  search: { maxAttempts: 30, windowMs: 60_000 },
  bugReport: { maxAttempts: 3, windowMs: 300_000 },
  enquiry: { maxAttempts: 5, windowMs: 3_600_000 },
  contact: { maxAttempts: 3, windowMs: 300_000 },
  phoneOtp: { maxAttempts: 3, windowMs: 300_000 },
  // Direct CarJam browser calls — mirrors the limit we used to enforce
  // server-side before we dropped the Cloud Function proxy.
  carjam: { maxAttempts: 5, windowMs: 60_000 },
} satisfies Record<string, RateLimitConfig>

type RateLimitKey = keyof typeof RATE_LIMITS

const STORAGE_PREFIX = 'isqroll_rl_'

function getAttempts(key: RateLimitKey): number[] {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`)
    if (!raw) return []
    return JSON.parse(raw) as number[]
  } catch {
    return []
  }
}

function setAttempts(key: RateLimitKey, timestamps: number[]): void {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(timestamps))
  } catch {
    // sessionStorage full or unavailable — fail open
  }
}

/** Check if action is allowed. Returns true if ALLOWED, false if BLOCKED. */
export function checkRateLimit(key: RateLimitKey): boolean {
  const config = RATE_LIMITS[key]
  const now = Date.now()
  const recent = getAttempts(key).filter((t) => t > now - config.windowMs)
  setAttempts(key, recent)
  return recent.length < config.maxAttempts
}

/** Record an attempt. Call after the action is attempted. */
export function recordAttempt(key: RateLimitKey): void {
  const config = RATE_LIMITS[key]
  const now = Date.now()
  const recent = getAttempts(key).filter((t) => t > now - config.windowMs)
  recent.push(now)
  setAttempts(key, recent)
}

/** Get remaining attempts before rate limit. */
export function getRemainingAttempts(key: RateLimitKey): number {
  const config = RATE_LIMITS[key]
  const recent = getAttempts(key).filter((t) => t > Date.now() - config.windowMs)
  return Math.max(0, config.maxAttempts - recent.length)
}
